import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ShareFat } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { SearchInput, SuggestionSearch } from "@/components/ui/search"
import { WorkingUserOptions } from "@/components/working-user-options"
import { AgentOption } from "@/hooks/useAgentOptions"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useShareMedicalRecord } from "@/modules/patients/services/useShareMedicalRecord"

export const ShareMedicalRecord = ({ dropdownAlign = "center" }: { dropdownAlign?: "center" | "end" | "start" }) => {
  const { t } = useTranslation()
  const [openDialog, setOpenDialog] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [shouldLoadWorkingUsers, setShouldLoadWorkingUsers] = useState(false)
  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()

  const commit = useShareMedicalRecord()

  const handleSelectOption = (option: AgentOption) => {
    commit({ appointmentId, patientId, userNameToShared: option.value }, option.label)
    setSearchValue("")
    setOpenDialog(false)
  }

  return (
    <SuggestionSearch className="w-fit">
      <DropdownMenu
        modal={false}
        open={openDialog}
        onOpenChange={(open) => {
          setOpenDialog(open)
          if (open) {
            setShouldLoadWorkingUsers(true)
          }
        }}>
        <DropdownMenuTrigger className="outline-hidden">
          <Button variant="circle" className="relative h-10 w-10" data-testid="share-medical-record-btn" asChild>
            <ShareFat className="text-on-surface-subdued disabled:text-on-surface-disabled" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="rounded-lg" align={dropdownAlign} dataTestId="share-medical-record-dropdown">
          <div className="border-border mb-2 border-b p-2">
            <SearchInput
              onKeyDown={(e) => {
                e.stopPropagation()
              }}
              data-testid="share-medical-record-input"
              value={searchValue}
              placeholder={t("patient.shareMedicalRecord.shareWith")}
              autoComplete="off"
              onChange={({ target: { value } }) => {
                setSearchValue(value)
              }}
              onClear={() => {
                setSearchValue("")
              }}
            />
          </div>
          <div className="border-border max-h-80 border-b" data-testid="share-medical-record-search-section">
            <WorkingUserOptions
              skip={!shouldLoadWorkingUsers}
              searchValue={searchValue}
              onSelectOption={handleSelectOption}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </SuggestionSearch>
  )
}
